import { useEffect, useMemo, useRef } from 'react';

import { useFieldContext } from '../../hooks/use-form-context';
import { debounce } from '../../lib/utils';

export type AutoSaveEffectProps<TValue = unknown> = {
    /**
     * The patch function from usePatcher, e.g., patchField('FieldName')
     * Signature: ({ value }: { value: TValue }) => Promise<string | undefined>
     */
    patchField: (args: { value: TValue }) => Promise<string | undefined>;

    /**
     * Debounce delay in milliseconds. Defaults to 500ms.
     */
    debounceMs?: number;

    /**
     * Optional transformation function applied to the value before patching.
     * Useful for format conversions (e.g., Date to ISO string).
     */
    transformValue?: (value: TValue) => unknown;
};

/**
 * Headless component that auto-saves field values to the API.
 * Must be used as a child of form.Field.
 *
 * Features:
 * - Debounced API calls (default 500ms)
 * - Dirty check to prevent unnecessary patches
 * - Cleanup on unmount (cancels pending debounced calls)
 * - Optional value transformation
 *
 * @example
 * <form.Field name="name" validators={{ onChange: validators.name }}>
 *     {(field) => (
 *         <>
 *             <field.Input />
 *             <field.AutoSave patchField={patchField('Name')} />
 *         </>
 *     )}
 * </form.Field>
 */
export function AutoSaveEffect<TValue = unknown>({
    patchField,
    debounceMs = 500,
    transformValue,
}: AutoSaveEffectProps<TValue>): null {
    const field = useFieldContext<TValue>();
    const currentValue = field.state.value;

    // Track the last saved value to implement dirty checking
    const lastSavedValueRef = useRef<TValue>(currentValue);

    // Track if this is the initial mount to skip first render
    const isInitialMountRef = useRef(true);

    // Create debounced patch function
    const debouncedPatch = useMemo(() => {
        const patchFn = async (value: TValue) => {
            // Apply transformation if provided
            const valueToSend = transformValue ? transformValue(value) : value;

            const error = await patchField({ value: valueToSend as TValue });

            // Only update last saved value on success
            if (!error) {
                lastSavedValueRef.current = value;
            }
        };

        return debounce(patchFn, debounceMs);
    }, [patchField, debounceMs, transformValue]);

    // Effect to trigger save on value change
    useEffect(() => {
        // Skip the initial mount
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }

        // Dirty check: only patch if value has changed from last saved
        if (currentValue === lastSavedValueRef.current) {
            return;
        }

        debouncedPatch(currentValue);
    }, [currentValue, debouncedPatch]);

    // Cleanup: cancel pending debounced calls on unmount
    useEffect(() => {
        return () => {
            debouncedPatch.cancel();
        };
    }, [debouncedPatch]);

    return null;
}

AutoSaveEffect.displayName = 'AutoSaveEffect';
